import { api } from "@config/axios/axios.config";
import { showNotification } from "@libs/notistack/notificationStack";
import { SERVER_ROUTES } from "@constants/api.routes";
import { HTTP_STATUS } from "@constants/http.status.code";
import { SNACKBAR_SEVERITY } from "@constants/notificationStack";
import { getTranslation } from "@utils/translation";
import type { NotificationsResponse } from "@components/Notification/types/types";

export async function fetchNotificationsService(page: number = 1, limit: number = 10) {
	const res = await api.get<NotificationsResponse>(SERVER_ROUTES.NOTIFICATIONS.NOTIFICATIONS, {
		params: { page, limit },
	});
	if (res?.data && res?.data?.success === false) {
		showNotification({
			message: res.data?.message || getTranslation("psdaCommon.notifications.api.errors.failed"),
			severity: SNACKBAR_SEVERITY.ERROR,
		});
		throw new Error(
			res.data?.message || getTranslation("psdaCommon.notifications.api.errors.fetchNotifications.failed"),
		);
	}

	// Success
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	if ((res as any)?.statusCode === HTTP_STATUS.OK || (res as any)?.statusCode === HTTP_STATUS.CREATED) {
		// Handle the new nested response structure
		const responseData = res as unknown as NotificationsResponse;

		// Return the data in the expected format for backward compatibility
		return {
			data: responseData?.data?.data, // Access the data array directly
			unreadCount: responseData?.data?.unreadCount,
			pagination: {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				page: (responseData as any)?.data?.pagination?.currentPage,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				hasNextPage: (responseData as any)?.data?.pagination?.hasNextPage,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				totalItems: (responseData as any)?.data?.pagination?.totalItems,
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				totalPages: (responseData as any)?.data?.pagination?.totalPages,
			},
		};
	}
}

// Service to fetch only unread count without affecting the current notification list
export async function fetchUnreadCountService() {
	try {
		const res = await api.get<NotificationsResponse>(SERVER_ROUTES.NOTIFICATIONS.NOTIFICATIONS, {
			params: { page: 1, limit: 1 }, // Only fetch 1 notification to get the unread count
		});

		if (res?.data && res?.data?.success === false) {
			console.error("Failed to fetch unread count:", res.data?.message);
			return null;
		}

		// Success
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		if ((res as any)?.statusCode === HTTP_STATUS.OK || (res as any)?.statusCode === HTTP_STATUS.CREATED) {
			const responseData = res as unknown as NotificationsResponse;
			return responseData?.data?.unreadCount || 0;
		}

		return null;
	} catch (error) {
		console.error("Error fetching unread count:", error);
		return null;
	}
}
